// =========================================================
// Vojjin Errors Wrapper
// =========================================================
import {registerGlobal} from "./core.js";

class CanvasSlider {
    constructor(opts = {}) {
        // Canvas & images
        this.canvas = typeof opts.canvas === "string" ? document.querySelector(opts.canvas) : opts.canvas;
        if (!this.canvas) throw new Error("Canvas not found");
        this.ctx = this.canvas.getContext("2d");

        this.images = (opts.images || []).map(img => ({
            src: img.src,
            link: img.link || null,
            element: null,
        }));

        // Settings
        this.visibleCount = opts.visibleCount ?? 3;
        this.gap = opts.gap ?? 16;
        this.ratio = opts.ratio ?? (16 / 9);
        this.transitionDuration = opts.transitionDuration ?? 1000;
        this.displayDuration = opts.displayDuration ?? 3000;
        this.autoplay = opts.autoplay ?? true;
        this.direction = opts.direction ?? "left";
        this.transition = opts.transition ?? "slide";
        this.controls = opts.controls ?? false;
        this.bullets = opts.bullets ?? false;
        this.imageSize = opts.imageSize || {width: 1, height: 1}; // default 1:1 ratio
        this.ratioFn = opts.ratioFn ?? null;
        this.orientation = opts.orientation ?? "horizontal"; // or "vertical"
        this.easing = opts.easing ?? (this.transition === "fade" ? "easeInOutSine" : "easeInOutQuad");

        if (typeof this.ratioFn !== "function") {
            this.ratioFn = function ({width, visibleCount, gap}) {
                const totalGap = (visibleCount - 1) * gap;
                const aspectRatio = this.imageSize.width / this.imageSize.height;

                if (this.orientation === "vertical") {
                    // Vertical layout — stack images top to bottom
                    const totalImageHeight = width - totalGap; // here width acts as vertical space
                    const oneImageHeight = totalImageHeight / visibleCount;
                    const oneImageWidth = oneImageHeight * aspectRatio;
                    return oneImageWidth / width;
                } else {
                    // Horizontal layout — side by side
                    const totalImageWidth = width - totalGap;
                    const oneImageWidth = totalImageWidth / visibleCount;
                    const oneImageHeight = oneImageWidth / aspectRatio;
                    return width / oneImageHeight;
                }
            };
        }

        // State
        this.index = 0;
        this.currentOffset = 0;
        this.running = false;
        this.transitionStart = null;
        this.nextTransition = 0;
        this.lastTime = null;
        this.paused = false;
        this.pauseTime = 0;

        this._resize = this.resize.bind(this);
        window.addEventListener("resize", this._resize);
        this.resize();

        // Event bindings
        this.canvas.addEventListener("mouseenter", () => this.pause());
        this.canvas.addEventListener("mouseleave", () => this.resume());
        this.canvas.addEventListener("click", e => this.handleClick(e));

        this.initSwipe();

        // Controls (arrows + bullets)
        if (this.controls) this.createControls();
        if (this.bullets) this.createBullets();

        // Start
        this.preloadImages().then(loaded => {
            if (loaded > 0 && this.autoplay) this.start();
        });
    }

    getDirectionVector() {
        // Horizontal: left = -1, right = +1
        // Vertical: left = -1 (up), right = +1 (down)
        return this.direction === "left" ? -1 : 1;
    }

    resize({visibleCount, gap, ratio, orientation} = {}) {
        if (typeof visibleCount === "number") this.visibleCount = visibleCount;
        if (typeof gap === "number") this.gap = gap;
        if (typeof ratio === "number") this.ratio = ratio;
        if (typeof orientation === "string") this.orientation = orientation;

        const parent = this.canvas.parentElement;
        if (!parent) return;

        // Determine available area (minus padding)
        const style = getComputedStyle(parent);
        const padL = parseFloat(style.paddingLeft) || 0;
        const padR = parseFloat(style.paddingRight) || 0;
        const availableWidth = parent.clientWidth - padL - padR;

        // Compute ratio and height
        const computedRatio =
            (typeof this.ratioFn === "function"
                ? this.ratioFn.call(this, {
                    width: availableWidth,
                    visibleCount: this.visibleCount,
                    gap: this.gap,
                })
                : this.ratio) || 1;

        let availableHeight;

        if (this.orientation === "vertical") {
            // For vertical layout, ratio = imageWidth / imageHeight
            // so height = width / ratio
            availableHeight = availableWidth / computedRatio;
        } else {
            // For horizontal layout, ratio = width / height
            availableHeight = availableWidth / computedRatio;
        }
        // Retina-safe scaling
        const scale = window.devicePixelRatio || 1;
        this.canvas.width = availableWidth * scale;
        this.canvas.height = availableHeight * scale;

        // Critical fix: Set visual CSS size (in CSS pixels)
        this.canvas.style.width = `${availableWidth}px`;
        this.canvas.style.height = `${availableHeight}px`;

        // Reset and rescale context
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(scale, scale);

        this.draw();
    }

    async preloadImages() {
        let loadedCount = 0;
        const promises = this.images.map(img => new Promise(resolve => {
            const image = new Image();
            image.onload = () => {
                img.element = image;
                loadedCount++;
                resolve();
            };
            image.onerror = () => resolve();
            image.src = img.src;
        }));
        await Promise.all(promises);
        return loadedCount;
    }

    start() {
        if (this.running || !this.images.length) return;
        this.running = true;
        this.lastTime = performance.now();
        this.nextTransition = this.lastTime + this.displayDuration;
        this.animate();
    }

    stop() {
        this.running = false;
        cancelAnimationFrame(this.raf);
    }

    pause() {
        if (this.running && !this.paused) {
            this.paused = true;
            this.pauseTime = performance.now();
        }
    }

    resume() {
        if (!this.running || !this.paused) return;
        this.paused = false;
        const delta = performance.now() - this.pauseTime;
        this.transitionStart &&= this.transitionStart + delta;
        this.transitionEnd &&= this.transitionEnd + delta;
        this.nextTransition += delta;
        this.lastTime += delta;
    }

    animate(timestamp = performance.now()) {
        if (!this.running) return;
        if (this.paused) {
            this.raf = requestAnimationFrame(this.animate.bind(this));
            return;
        }

        const {width, height} = this.canvas;
        const totalGap = this.gap * (this.visibleCount - 1);

        // Compute image size depending on orientation
        let imgWidth, imgHeight;
        if (this.orientation === "horizontal") {
            imgWidth = (width / window.devicePixelRatio - totalGap) / this.visibleCount;
            imgHeight = height / window.devicePixelRatio;
        } else {
            imgWidth = width / window.devicePixelRatio;
            imgHeight = (height / window.devicePixelRatio - totalGap) / this.visibleCount;
        }

        const step = this.orientation === "horizontal"
            ? imgWidth + this.gap
            : imgHeight + this.gap;

        if (!this.lastTime) this.lastTime = timestamp;
        this.lastTime = timestamp;

        // Transition timing
        if (!this.transitionStart && timestamp > this.nextTransition) {
            this.transitionStart = timestamp;
            this.transitionEnd = timestamp + this.transitionDuration;
            this.startOffset = 0;
            this.nextTransition = timestamp + this.displayDuration + this.transitionDuration;
        }

        if (this.transitionStart) {
            let progress = (timestamp - this.transitionStart) / this.transitionDuration;

            if (progress >= 1) {
                progress = 1;
                this.transitionStart = null;
                this.index =
                    (this.index + (this.direction === "right" ? -1 : 1) + this.images.length) %
                    this.images.length;
                this.currentOffset = 0;
                this.updateBullets();
                this.draw();
                this.raf = requestAnimationFrame(this.animate.bind(this));
                return;
            }

            const eased = Easing.get(this.easing)(progress);

            if (this.transition === "fade") {
                this.currentOffset = eased; // for opacity
            } else {
                const step = this.orientation === "horizontal"
                    ? (imgWidth + this.gap)
                    : (imgHeight + this.gap);

                this.currentOffset = step * eased;
            }
        }

        this.draw();
        this.raf = requestAnimationFrame(this.animate.bind(this));
    }

    draw() {
        const ctx = this.ctx;
        const {width, height} = this.canvas;
        ctx.clearRect(0, 0, width, height);

        if (!this.images.length) return;

        const totalGap = this.gap * (this.visibleCount - 1);
        const imgWidth = (width / window.devicePixelRatio - totalGap) / this.visibleCount;
        const step = imgWidth + this.gap;
        const totalImages = this.images.length;
        const baseIndex = ((this.index % totalImages) + totalImages) % totalImages;

        if (this.transition === "fade") {
            // --- FADE TRANSITION ---
            const nextIndex = (this.index + (this.direction === "right" ? -1 : 1) + totalImages) % totalImages;
            const current = this.images[baseIndex]?.element;
            const next = this.images[nextIndex]?.element;

            if (!current || !next) return;

            // Draw current slide (fading out)
            ctx.globalAlpha = 1 - this.currentOffset;
            ctx.drawImage(current, 0, 0, width / window.devicePixelRatio, height / window.devicePixelRatio);

            // Draw next slide (fading in)
            ctx.globalAlpha = this.currentOffset;
            ctx.drawImage(next, 0, 0, width / window.devicePixelRatio, height / window.devicePixelRatio);

            ctx.globalAlpha = 1; // reset alpha
            return;
        }

        // --- SLIDE TRANSITION ---
        if (this.transition === "slide") {
            for (let i = 0; i <= this.visibleCount; i++) {
                const index = (this.index + i) % this.images.length;
                const imgObj = this.images[index];
                if (!imgObj?.element) continue;

                const totalGap = this.gap * (this.visibleCount - 1);
                let imgWidth, imgHeight;

                if (this.orientation === "horizontal") {
                    imgWidth = (width / window.devicePixelRatio - totalGap) / this.visibleCount;
                    imgHeight = imgWidth * (this.imageSize.height / this.imageSize.width);
                } else {
                    imgHeight = (height / window.devicePixelRatio - totalGap) / this.visibleCount;
                    imgWidth = imgHeight * (this.imageSize.width / this.imageSize.height);
                }

                const step = this.orientation === "horizontal"
                    ? imgWidth + this.gap
                    : imgHeight + this.gap;

                const dir = this.getDirectionVector(); // -1 for left/up, +1 for right/down

                let offset = this.currentOffset * dir;

                for (let i = -1; i <= this.visibleCount; i++) {
                    const index = (this.index + i + this.images.length) % this.images.length;
                    const imgObj = this.images[index];
                    if (!imgObj?.element) continue;

                    let x = 0, y = 0;

                    if (this.orientation === "horizontal") {
                        x = (i * step) + offset;
                    } else {
                        y = (i * step) + offset;
                    }

                    this.ctx.drawImage(imgObj.element, x, y, imgWidth, imgHeight);
                }
            }
        }
    }

    handleClick(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = this.canvas.width / window.devicePixelRatio;
        const totalGap = this.gap * (this.visibleCount - 1);
        const imgWidth = (width - totalGap) / this.visibleCount;
        const step = imgWidth + this.gap;
        const index = (this.index + Math.floor(x / step)) % this.images.length;
        const link = this.images[index].link;
        if (link) window.open(link, "_blank");
    }

    createControls() {
        const left = document.createElement("button");
        const right = document.createElement("button");
        left.textContent = "‹";
        right.textContent = "›";
        left.className = "slider-arrow left";
        right.className = "slider-arrow right";

        left.onclick = () => this.slide("right");
        right.onclick = () => this.slide("left");

        this.canvas.parentElement.append(left, right);
    }

    createBullets() {
        const container = document.createElement("div");
        container.className = "slider-bullets";
        this.bulletEls = this.images.map((_, i) => {
            const b = document.createElement("span");
            b.className = "bullet";
            b.onclick = () => this.goTo(i);
            container.appendChild(b);
            return b;
        });
        this.canvas.parentElement.append(container);
        this.updateBullets();
    }

    updateBullets() {
        if (!this.bulletEls) return;
        this.bulletEls.forEach((b, i) => {
            b.classList.toggle("active", i === this.index);
        });
    }

    slide(dir) {
        if (dir === "left") this.direction = "left";
        else this.direction = "right";
        this.transitionStart = performance.now();
        this.transitionEnd = this.transitionStart + this.transitionDuration;
    }

    goTo(i) {
        this.index = i % this.images.length;
        this.transitionStart = null;
        this.currentOffset = 0;
        this.updateBullets();
        this.draw();
    }

    initSwipe() {
        let startX = 0, distX = 0;
        this.canvas.addEventListener("touchstart", e => startX = e.touches[0].clientX);
        this.canvas.addEventListener("touchmove", e => distX = e.touches[0].clientX - startX);
        this.canvas.addEventListener("touchend", () => {
            if (Math.abs(distX) > 50) {
                this.slide(distX > 0 ? "right" : "left");
            }
            distX = 0;
        });
    }
}

registerGlobal("CanvasSlider", CanvasSlider);

